<template>

  <div class="register-form-shell w-full flex flex-col overflow-hidden bg-white">
    
    <!-- 1. 固定头部：标题与温馨提示 (shrink-0 保证不被压缩) -->
    <div class="register-form-head shrink-0 px-1 pt-1">
      <div class="mb-2">
        <h2 class="register-form-title text-[22px] font-extrabold text-[#111827] mb-1 tracking-tight">创建专属账号</h2>
        <p class="register-form-desc text-[12px] text-[#6b7280] leading-relaxed">为了向您提供精准的价格及开票服务，请填写真实的机构信息。</p>
      </div>

      <!-- 温馨提示框：优化图标对齐 -->
      <div class="register-tip bg-[#ecfdf5] border border-[#a7f3d0] text-[#059669] px-3 py-1.5 rounded-lg text-[12px] mb-2 flex items-center gap-2 shadow-sm">
        <el-icon class="shrink-0 text-[#10b981] text-[18px]"><InfoFilled /></el-icon>
        <span class="leading-relaxed">
          温馨提示：注册成功后，您的 <strong class="font-bold">手机号码</strong> 和 <strong class="font-bold">电子邮箱</strong> 均可直接作为账号登录平台。
        </span>
      </div>
    </div>

    <!-- 2. 核心表单区：独立滚动 (flex-1 占据剩余空间) -->
    <el-form 
      ref="formRef"
      :model="registerForm" 
      :rules="rules"
      label-position="top"
      size="small"
      @submit.prevent="handleRegister"
      class="register-form flex-1 min-h-0 flex flex-col"
    >
      <!-- 滚动容器：pr-4 预留滚动条空间，防止遮挡 -->
      <div class="register-scroll-area flex-1 overflow-y-auto custom-scrollbar pr-4 -mr-2">
                <!-- 联系人姓名：保留校验提示空间，避免为空时错误提示遮挡下一项 -->
        <el-form-item label="联系人姓名" prop="customerName" class="!mb-5">
          <el-input 
            v-model="registerForm.customerName" 
            placeholder="请输入您的真实姓名" 
            :prefix-icon="User" 
            clearable 
          />
        </el-form-item>

        <!-- 手机号码 -->
        <el-form-item label="手机号码" prop="mobile" class="!mb-5">
          <el-input 
            v-model="registerForm.mobile" 
            placeholder="请输入 11 位手机号码" 
            :prefix-icon="Iphone" 
            clearable 
          />
        </el-form-item>

        <!-- 短信验证码 -->
        <el-form-item label="短信验证码" prop="code" class="!mb-5">
          <div class="code-input-group w-full">
            <el-input 
              v-model="registerForm.code" 
              placeholder="6 位验证码" 
              :prefix-icon="ChatLineRound" 
              clearable 
            />
            <el-button plain :disabled="isCountingDown" @click="sendVerificationCode">
              {{ countdown > 0 ? `${countdown}s 后重试` : "获取验证码" }}
            </el-button>
          </div>
        </el-form-item>

        <!-- 电子邮箱 -->
        <el-form-item label="电子邮箱" prop="mail" class="!mb-5">
          <el-input 
            v-model="registerForm.mail" 
            placeholder="请输入常用邮箱地址" 
            :prefix-icon="Message" 
            clearable 
          />
        </el-form-item>

        <!-- 设置密码 -->
        <el-form-item label="设置登录密码" prop="password" class="!mb-5">
          <el-input 
            v-model="registerForm.password" 
            placeholder="8位以上字母与数字组合" 
            :prefix-icon="Lock" 
            show-password 
            clearable 
          />
        </el-form-item>

        <!-- 机构名称 -->
        <el-form-item label="机构名称 (学校院系或企业全称)" prop="customerCompany" class="!mb-3">
          <el-input 
            v-model="registerForm.customerCompany" 
            placeholder="例如：复旦大学 药学院 或 上海普洛汇生物科技有限公司"
            :prefix-icon="OfficeBuilding" 
            clearable 
          />
        </el-form-item>
        
        <div class="text-[12px] text-gray-400 mt-0 mb-2 pl-1 flex items-center gap-1.5">
          <el-icon class="text-[15px]"><Warning /></el-icon> 
          <span>提交后将用于后续的危化品及易制毒采购资质核验</span>
        </div>
      </div>

      <!-- 3. 固定底部：提交按钮 (shrink-0 固定位置) -->
      <div class="register-footer shrink-0 pt-2 border-t border-gray-100 bg-white">
        <el-button 
          type="primary" 
          :loading="loading" 
          @click="handleRegister"
          class="register-submit-btn !h-[44px] !text-[15px] !font-bold"
        >
          提交注册并继续
        </el-button>

        <div class="register-switch-row text-center mt-2 text-[13px] text-gray-500 pb-1">
          已有 普洛汇 账号？ 
          <span 
            class="register-switch-link text-emerald-600 font-bold cursor-pointer hover:text-emerald-500 transition-colors ml-1"
            @click="emit('onLogin')"
          >
            返回登录
          </span>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import { ElMessage } from "element-plus";
import { getLoginCodeApi, registerApi } from "@/apis/login";
import {
  OfficeBuilding,
  Iphone,
  Message,
  ChatLineRound,
  Lock,
  User,
  InfoFilled,
  Warning
} from "@element-plus/icons-vue";

const emit = defineEmits(["onLogin"]);
const formRef = ref(null); 

const registerForm = reactive({
  customerName: "",
  contactPhone: "",
  customerCompany: "",
  mobile: "",
  mail: "",
  code: "",
  password: ""
});

const rules = reactive({
  customerName: [{ required: true, message: "请输入联系人姓名", trigger: "blur" }],
  customerCompany: [{ required: true, message: "请输入单位名称", trigger: "blur" }],
  mail: [
    { required: true, message: "请输入邮箱", trigger: "blur" },
    { type: "email", message: "请输入正确的邮箱地址", trigger: ["blur", "change"] }
  ],
  mobile: [
    { required: true, message: "请输入手机号", trigger: "blur" },
    { pattern: /^1[3-9]\d{9}$/, message: "手机号格式不正确", trigger: "blur" }
  ],
  code: [{ required: true, message: "请输入验证码", trigger: "blur" }],
  password: [
    { required: true, message: "请设置密码", trigger: "blur" },
    { min: 6, max: 20, message: "密码长度在 8 到 20 个字符", trigger: "blur" }
  ]
});

const loading = ref(false);
const countdown = ref(0);
const isCountingDown = ref(false);
let countdownTimer = null;

const sendVerificationCode = async () => {
  formRef.value.validateField('mobile', async (valid) => {
    if (!valid) return;
    try {
      await getLoginCodeApi(registerForm.mobile);
      ElMessage.success("验证码已发送");
      isCountingDown.value = true;
      countdown.value = 60;
      countdownTimer = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(countdownTimer);
          isCountingDown.value = false;
        }
      }, 1000);
    } catch (error) {
      if (!error?.isAuthError) {
        ElMessage.error(error?.message || "验证码发送失败");
      }
    }
  });
};

const handleRegister = async () => {
  if (!formRef.value) return;
  await formRef.value.validate(async (valid) => {
    if (!valid) {
      ElMessage.warning("请完善注册信息");
      return;
    }
    loading.value = true;
    try {
      const submitData = { ...registerForm, contactPhone: registerForm.mobile };
      const res = await registerApi(submitData);
      if (res.code === 200) {
        ElMessage.success("注册成功，请登录");
        emit("onLogin");
      } else {
        ElMessage.error(res.message || "注册失败");
      }
    } catch (err) {
      ElMessage.error(typeof err === 'string' ? err : '注册发生错误');
    } finally {
      loading.value = false;
    }
  });
};
</script>

<style scoped>
.register-form-shell {
  height: 100%;
  min-height: 0;
}

.register-form {
  min-height: 0;
}

.register-form-title {
  line-height: 1.25;
}

.register-tip {
  align-items: flex-start;
}

.register-scroll-area {
  min-height: 0;
  padding-bottom: 2px;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.register-footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 0 4px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.72) 0%, #ffffff 24%),
    #ffffff;
}

.register-submit-btn {
  width: 100%;
  margin-left: 0 !important;
  border: 0 !important;
  border-radius: 10px !important;
  background: linear-gradient(135deg, #1294ff 0%, #0077d9 100%) !important;
  box-shadow: 0 10px 22px rgba(0, 137, 238, 0.22);
  letter-spacing: 0;
}

.register-submit-btn:hover {
  background: linear-gradient(135deg, #007ff0 0%, #006bc7 100%) !important;
  box-shadow: 0 12px 24px rgba(0, 137, 238, 0.28);
}

.register-switch-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 0 !important;
  padding-bottom: 0 !important;
  line-height: 1.5;
}

.register-switch-link {
  margin-left: 0 !important;
  padding: 2px 4px;
  border-radius: 6px;
  color: #047857 !important;
}

.register-switch-link:hover {
  background: #ecfdf5;
  color: #059669 !important;
}

.code-input-group {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.code-input-group .el-input {
  flex: 1;
  min-width: 0;
}

.code-input-group .el-button {
  flex: 0 0 96px;
  min-width: 96px;
  padding: 0 12px;
}

:deep(.el-form-item__label) {
  min-height: auto;
  line-height: 1.35;
  padding-bottom: 4px;
  font-size: 13px;
}

:deep(.el-input__wrapper) {
  min-height: 32px;
}

/* 针对表单内部滚动区域的滚动条美化 */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  border: 2px solid #ffffff;
  border-radius: 999px;
  background-color: #cbd5e1;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

/* 注册页表单项需要给 Element Plus 校验提示预留高度，防止输入为空时提示文案覆盖下一行输入框 */
:deep(.el-form-item__error) {
  line-height: 16px;
  padding-top: 3px;
}

@media (max-height: 720px) {
  .register-form-title {
    font-size: 20px;
  }

  .register-tip {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  :deep(.el-form-item) {
    margin-bottom: 16px !important;
  }

  .register-footer {
    padding-top: 8px;
  }
}

@media (max-height: 600px) {
  .register-form-title {
    font-size: 18px;
  }

  .register-tip {
    margin-bottom: 6px;
  }

  :deep(.el-form-item) {
    margin-bottom: 14px !important;
  }
}

@media (max-width: 480px) {
  .register-form-shell {
    border-radius: 16px;
  }

  .register-form-head {
    padding: 0 2px;
  }

  .register-form-head > .mb-2 {
    margin-bottom: 8px;
  }

  .register-form-title {
    font-size: 20px;
  }

  .register-form-desc {
    font-size: 12px;
  }

  .register-tip {
    margin-bottom: 10px;
    padding: 8px 10px;
    border-radius: 10px;
  }

  .register-scroll-area {
    padding-right: 4px !important;
    margin-right: 0 !important;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    border: 0;
    background-color: #d6e1ec;
  }

  :deep(.el-form-item) {
    margin-bottom: 13px !important;
  }

  :deep(.el-form-item__label) {
    padding-bottom: 5px;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
  }

  :deep(.el-input__wrapper) {
    min-height: 40px;
    border: 1px solid #e5edf5;
    border-radius: 10px;
    background: #f8fafc;
    box-shadow: none !important;
  }

  :deep(.el-input__wrapper.is-focus) {
    border-color: #1294ff;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(18, 148, 255, 0.1) !important;
  }

  .code-input-group {
    flex-direction: column;
    gap: 8px;
  }

  .code-input-group .el-button {
    width: 100%;
    min-height: 40px;
    flex: 0 0 auto;
    border-radius: 10px;
    font-weight: 700;
  }

  .register-footer {
    padding-top: 10px;
    box-shadow: 0 -10px 22px rgba(255, 255, 255, 0.95);
  }

  .register-submit-btn {
    height: 42px !important;
    border-radius: 12px !important;
  }

  .register-footer :deep(.el-button) {
    width: 100%;
  }

  .register-switch-row {
    font-size: 12px;
  }
}
</style>
